import { useQuery, useMutation, useQueryClient, QueryKey } from "@tanstack/react-query";
import {
  fetchContactByIdentity,
  fetchConversationParticipants,
  fetchConversations,
  fetchConversation,
  fetchMessages,
  createMessage,
  fetchContactTags,
  createContactTag,
  deleteContactTag,
  fetchContacts,
  fetchContactDetail,
  createContactSummary,
  fetchTagByName,
  fetchEmails,
  fetchEmail,
  fetchEstimate,
  fetchFiles,
  fetchFile,
  fetchMessageTags,
  fetchMessageMedia,
  createMessageTag,
  deleteMessageTag,
  deleteConversation,
  createConversation,
  deleteFile,
} from "./ApiEndpoints.ts";
import { ContactModel, Message, TagRefModel, ContactSummary, Conversation, FileItem, UploadStatus } from "./Types.ts";
import { uploadViaXHR } from "./Upload.ts";

export interface ContactProperties {
  searchName: string;
  displayName: string;
  id: string;
  fullName: string;
  org: string;
  role: string;
  title: string;
  emails: string[];
  phones: string[];
  addresses: any[];
  photos: string[];
  urls: string[];
  notes: string[];
  n: any[];
  summary: ContactSummary;
  model: ContactModel;
}

function toContactProperties(c: ContactModel): ContactProperties {
  const properties: ContactProperties = {
    searchName: "",
    displayName: "",
    id: c.id,
    fullName: "",
    org: "",
    role: "",
    title: "",
    emails: [],
    phones: [],
    addresses: [],
    photos: [],
    urls: [],
    notes: [],
    n: [],
    summary: {} as ContactSummary,
    model: c,
  };

  try {
    if (c.summary) properties.summary = JSON.parse(c.summary) as ContactSummary;
  } catch (e) {
    console.error(e);
  }

  for (const p of c.jcard[1]) {
    const k = p[0];
    const v = p[3];

    if (k == "fn") {
      properties.fullName = v;
    } else if (k == "n") {
      const n = {
        family: v[0],
        given: v[1],
        additional: v[2],
        honorificPrefix: v[3],
        honorificSuffix: v[4],
      };
      properties.n.push(n);
    } else if (k == "org") {
      properties.org = v;
    } else if (k == "title") {
      properties.title = v;
    } else if (k == "role") {
      properties.role = v;
    } else if (k == "email") {
      properties.emails.push(v);
    } else if (k == "tel") {
      properties.phones.push(v);
    } else if (k == "addr") {
      const adr = {
        postOfficeBox: v[0],
        extendedAddress: v[1],
        streetAddress: v[2],
        locality: v[3],
        region: v[4],
        postalCode: v[5],
        countryName: v[6],
      };
      properties.addresses.push(adr);
    } else if (k == "note") {
      properties.notes.push(v);
    } else if (k == "photo") {
      properties.photos.push(v);
    } else if (k == "url") {
      properties.urls.push(v);
    }
  }

  properties.displayName = properties.fullName;
  if (!properties.displayName || properties.displayName == "") {
    if (properties.emails.length) properties.displayName = properties.emails[0];
  }

  properties.searchName = properties.displayName.toLowerCase() + " " + properties.org?.toLowerCase();

  return properties;
}

export function useContactByIdentity(id: string | undefined, jwt: string | undefined) {
  return useQuery({
    queryKey: ["fetchIdentity", { type: "contact", id: id }],
    queryFn: () => fetchContactByIdentity(id!, jwt!),
    enabled: Boolean(id && jwt),
    select: toContactProperties,
  });
}

/* conversations */
export function useConversationParticipants(conversationId: string | undefined, jwt: string | undefined) {
  return useQuery({
    queryKey: ["conversation", conversationId, "participants"],
    queryFn: () => fetchConversationParticipants(conversationId!, jwt!),
    enabled: Boolean(conversationId && jwt),
  });
}

export function useConversations(
  accountId: string | undefined,
  scope: string | undefined,
  type: string | undefined,
  jwt: string | undefined
) {
  return useQuery({
    queryKey: ["conversations", accountId, scope, type],
    queryFn: () => fetchConversations(scope!, type!, jwt!),
    enabled: Boolean(scope && type && jwt),
  });
}

export function useConversation(id: string | undefined, jwt: string | undefined) {
  return useQuery({
    queryKey: ["conversations", id],
    queryFn: () => fetchConversation(id!, jwt!),
    enabled: Boolean(id && jwt),
  });
}

export function useMessages(conversationId: string | undefined, jwt: string | undefined) {
  return useQuery({
    queryKey: ["messages", conversationId],
    queryFn: () => fetchMessages(conversationId!, jwt!),
    enabled: Boolean(conversationId && jwt),
  });
}

export function useCreateMessage(jwt?: string) {
  const queryClient = useQueryClient();

  return useMutation<
    Message, // what is returned by response
    Error, // error type
    Message, // variables to be mutated
    {
      key: (string | undefined)[];
      prev: Message[] | undefined;
      tmpId: string;
    }
  >({
    mutationFn: async (msg) => {
      return createMessage(msg, jwt!);
    },

    onMutate: async (tmpMsg) => {
      const key = ["messages", tmpMsg.conversation]; //key is also used by fetch
      await queryClient.cancelQueries({ queryKey: key });
      const prev = queryClient.getQueryData<Message[]>(key);

      // Push the temp message straight into the list
      queryClient.setQueryData<Message[]>(key, (old = []) => [tmpMsg, ...old]);
      return { key, prev, tmpId: tmpMsg.id };
    },

    onError: (_err, _vars, ctx) => {
      console.error("Error sending message: ", _err.message);
      if (ctx) {
        //if (ctx?.prev) queryClient.setQueryData(ctx.key, ctx.prev); //rollback
        queryClient.setQueryData<Message[]>(ctx.key, (old = []) =>
          old.map((m) => {
            //swap the tmp message with the saved message
            if (m.id === ctx.tmpId) m.error = true;
            return m;
          })
        );
      }
    },

    onSuccess: (saved, _vars, ctx) => {
      //try hard swap
      queryClient.setQueryData<Message[]>(ctx.key, [...(ctx.prev ?? []), saved]);
      /*
      queryClient.setQueryData<Message[]>(ctx.key, (old = []) =>
        old.map((m) => {
          if (m.id === ctx.tmpId) {
            //swap the tmp message with the saved message
            return saved;
          } else return m;
        })
      );*/
    },

    /*
    onSettled: (_data, _err, _vars, ctx) => {
      //if (ctx) queryClient.invalidateQueries({ queryKey: ctx.key });
    },*/
  });
}

export function useContactTags(contactId: string | undefined, jwt: string | undefined) {
  return useQuery({
    queryKey: ["contacts", "tags", contactId],
    queryFn: () => fetchContactTags(contactId!, jwt!),
    enabled: Boolean(contactId && jwt),
  });
}

export function useCreateContactTag(jwt?: string) {
  const queryClient = useQueryClient();

  return useMutation<
    TagRefModel, // what is returned by response
    Error, // error type
    { value: TagRefModel; contactId: string }, // params to be mutated
    { key: (string | undefined)[]; prev?: TagRefModel[]; path?: string }
  >({
    mutationFn: async (params) => {
      return createContactTag(params.contactId, params.value, jwt!);
    },

    onMutate: async ({ contactId, value }) => {
      const key = ["contacts", "tags", contactId];
      await queryClient.cancelQueries({ queryKey: key });
      const prev = queryClient.getQueryData<TagRefModel[]>(key);
      queryClient.setQueryData<TagRefModel[]>(key, (old = []) => [...old, value]);
      return { key, prev, path: value.path };
    },

    onError: (_e, _v, ctx) => {
      if (ctx?.prev) queryClient.setQueryData(ctx.key, ctx.prev);
    },

    onSuccess: (_saved, _v, ctx) => {
      if (_saved) {
        queryClient.setQueryData<TagRefModel[]>(ctx.key, (old = []) =>
          old.map((v) => {
            //set the id to the value returned by the saved ref
            v.id = v.path === ctx.path ? _saved.id : v.id;
            return v;
          })
        );
      }
    },
  });
}

export function useDeleteContactTag(jwt?: string) {
  const queryClient = useQueryClient();

  return useMutation<
    void, // server returns nothing on success
    Error, // error type
    { tagId: string; contactId: string },
    { key: (string | undefined)[]; prev?: TagRefModel[] }
  >({
    mutationFn: ({ tagId, contactId }) => deleteContactTag(contactId, tagId, jwt!),
    onMutate: async ({ contactId, tagId }) => {
      const key = ["contacts", "tags", contactId];
      await queryClient.cancelQueries({ queryKey: key });
      const prev = queryClient.getQueryData<TagRefModel[]>(key);
      queryClient.setQueryData<TagRefModel[]>(key, (old = []) => old.filter((t) => t.id !== tagId));

      return { key, prev }; // ctx for later
    },

    onError: (_err, _vars, ctx) => {
      console.error("Error deleting contact tag:", _err);
      if (ctx?.prev) queryClient.setQueryData(ctx.key, ctx.prev);
    },

    /*
    onSettled: (_d, _e, _v, ctx) => {
      if (ctx) queryClient.invalidateQueries({ queryKey: ctx.key });
    },*/
  });
}

export function useContacts(accountId: string | undefined, jwt: string | undefined) {
  return useQuery({
    queryKey: ["contacts", accountId],
    queryFn: () => fetchContacts(jwt!),
    enabled: Boolean(jwt),
    select: (data: ContactModel[]) => data.map(toContactProperties), // map each to ContactProperties
  });
}

export function useContactDetail(contactId: string | undefined, jwt: string | undefined) {
  return useQuery({
    queryKey: ["contacts", contactId],
    queryFn: () => fetchContactDetail(contactId!, jwt!),
    enabled: Boolean(contactId && jwt),
    select: toContactProperties,
  });
}

export function useCreateContactSummary(jwt?: string) {
  const queryClient = useQueryClient();

  return useMutation<
    ContactSummary, // what is returned by response
    Error, // error type
    ContactProperties, // params to be mutated
    { key: (string | undefined)[]; prev?: ContactProperties }
  >({
    mutationFn: async (contact) => {
      return createContactSummary(contact.id, jwt!);
    },

    onMutate: async (contact) => {
      const key = ["contacts", contact.id];
      await queryClient.cancelQueries({ queryKey: key });
      const prev = queryClient.getQueryData<ContactProperties>(key);
      //queryClient.setQueryData<ContactModel>(key, (old) => old);
      return { key, prev };
    },

    onError: (_e, _v, ctx) => {
      if (ctx?.prev) queryClient.setQueryData(ctx.key, ctx.prev);
    },

    onSuccess: (summary, _v, ctx) => {
      //queryClient.invalidateQueries({ queryKey: ctx.key });
      if (ctx?.prev) {
        //ctx.prev.summary = summary;
        const updated = { ...ctx.prev, summary: summary };
        queryClient.setQueryData(ctx.key, updated);
      }
    },
  });
}

export function useTag(name: string | undefined, accountId: string | undefined, jwt: string | undefined) {
  return useQuery({
    queryKey: ["tags", name, accountId],
    queryFn: () => fetchTagByName(name!, jwt!),
    enabled: !!name && !!accountId,
    gcTime: 1000 * 60 * 60 * 24, //garbage collect every 24 hours
    staleTime: 1000 * 60 * 60 * 24, // invalid every 24 hours,
    retry: 1,
    placeholderData: undefined,
  });
}

export function useEmails(accountId?: string, projectPath?: string, jwt?: string) {
  return useQuery({
    queryKey: ["emails", accountId, projectPath],
    queryFn: () => fetchEmails(projectPath!, jwt!),
    enabled: Boolean(jwt),
  });
}

export function useEmail(emailId: string | undefined, jwt: string | undefined) {
  return useQuery({
    queryKey: ["emails", emailId],
    queryFn: () => fetchEmail(emailId!, jwt!),
    enabled: Boolean(emailId && jwt),
  });
}

export function useMessageTags(messageId: string | undefined, jwt: string | undefined) {
  return useQuery({
    queryKey: ["messages", "tags", messageId],
    queryFn: () => fetchMessageTags(messageId!, jwt!),
    enabled: Boolean(messageId && jwt),
  });
}

export function useMessageMedia(messageId: string | undefined, jwt: string | undefined) {
  return useQuery({
    queryKey: ["messages", "media", messageId],
    queryFn: () => fetchMessageMedia(messageId!, jwt!),
    enabled: Boolean(messageId && jwt),
  });
}

export function useCreateMessageTag(jwt?: string) {
  const queryClient = useQueryClient();

  return useMutation<
    TagRefModel, // what is returned by response
    Error, // error type
    { value: TagRefModel; messageId: string }, // params to be mutated
    { key: (string | undefined)[]; prev?: TagRefModel[]; path?: string }
  >({
    mutationFn: async (params) => {
      return createMessageTag(params.messageId, params.value, jwt!);
    },

    onMutate: async ({ messageId, value }) => {
      const key = ["messages", "tags", messageId];
      await queryClient.cancelQueries({ queryKey: key });
      const prev = queryClient.getQueryData<TagRefModel[]>(key);
      queryClient.setQueryData<TagRefModel[]>(key, (old = []) => [...old, value]);
      return { key, prev, path: value.path };
    },

    onError: (_e, _v, ctx) => {
      if (ctx?.prev) queryClient.setQueryData(ctx.key, ctx.prev);
    },

    onSuccess: (_saved, _v, ctx) => {
      if (_saved) {
        queryClient.setQueryData<TagRefModel[]>(ctx.key, (old = []) =>
          old.map((v) => {
            //set the id to the value returned by the saved ref
            v.id = v.path === ctx.path ? _saved.id : v.id;
            return v;
          })
        );
      }
    },
  });
}

export function useDeleteMessageTag(jwt?: string) {
  const queryClient = useQueryClient();

  return useMutation<
    void, // server returns nothing on success
    Error, // error type
    { tagId: string; messageId: string },
    { key: (string | undefined)[]; prev?: TagRefModel[] }
  >({
    mutationFn: ({ tagId, messageId }) => deleteMessageTag(messageId, tagId, jwt!),
    onMutate: async ({ messageId, tagId }) => {
      const key = ["messages", "tags", messageId];
      await queryClient.cancelQueries({ queryKey: key });
      const prev = queryClient.getQueryData<TagRefModel[]>(key);
      queryClient.setQueryData<TagRefModel[]>(key, (old = []) => old.filter((t) => t.id !== tagId));
      return { key, prev }; // ctx for later
    },

    onError: (_err, _vars, ctx) => {
      if (ctx?.prev) queryClient.setQueryData(ctx.key, ctx.prev);
    },
  });
}

export function useDeleteConversation(accountId?: string, scope?: string, type?: string, jwt?: string) {
  const queryClient = useQueryClient();

  return useMutation<
    void, // server returns nothing on success
    Error, // error type
    { conversationId: string },
    { key: (string | undefined)[]; prev?: Conversation[] }
  >({
    mutationFn: ({ conversationId }) => deleteConversation(conversationId, jwt!),
    onMutate: async ({ conversationId }) => {
      const key = ["conversations", accountId, scope, type];
      await queryClient.cancelQueries({ queryKey: key });
      const prev = queryClient.getQueryData<Conversation[]>(key);
      queryClient.setQueryData<Conversation[]>(key, (old = []) => old.filter((c) => c.id !== conversationId));
      return { key, prev }; // ctx for later
    },

    onError: (_err, _vars, ctx) => {
      if (ctx?.prev) queryClient.setQueryData(ctx.key, ctx.prev);
    },
  });
}

export function useCreateConversation(accountId?: string, scope?: string, type?: string, jwt?: string) {
  const queryClient = useQueryClient();

  return useMutation<
    Conversation, // what is returned by response
    Error, // error type
    { name: string }, // params to be mutated
    { key: (string | undefined)[]; prev?: Conversation[] }
  >({
    mutationFn: async (params) => {
      return createConversation(params.name, type!, jwt!);
    },

    /*
    onMutate: async ({ name: string }) => {
      const key = ["conversations", accountId, scope, type];
      await queryClient.cancelQueries({ queryKey: key });
      const prev = queryClient.getQueryData<Conversation[]>(key);
      //queryClient.setQueryData<Conversation[]>(key, (old = []) => [...old, value]);
      return { key, prev };
    },*/

    onError: (_e, _v, ctx) => {
      if (ctx?.prev) queryClient.setQueryData(ctx.key, ctx.prev);
    },

    onSuccess: (_saved, _v) => {
      if (_saved) {
        const key = ["conversations", accountId, scope, type];
        queryClient.setQueryData<Conversation[]>(key, (old = []) => [...old, _saved]);
      }
    },
  });
}

export function useUploadFile(accountId: string, token: string) {
  const queryClient = useQueryClient();

  const updateProgress = async (pct: number) => {
    console.log("upload progress reported:", pct);
    const key = ["files", accountId];
    await queryClient.cancelQueries({ queryKey: key });
    //const prev = queryClient.getQueryData<FileItem[]>(key);
    queryClient.setQueryData<FileItem[]>(key, (old = []) =>
      old.map((v) => {
        console.log("old item status:", v.status);
        if (v.status === UploadStatus.Uploading || v.status === UploadStatus.Pending) {
          //set the id to the value returned by the saved ref
          console.log("setting upload progress to:", pct);
          v.progress = pct;
        }
        return v;
      })
    );
  };

  return useMutation<
    FileItem,
    Error,
    { file: File; item: FileItem },
    { key: (string | undefined)[]; prev?: FileItem[]; id: string }
  >({
    mutationFn: (params) => {
      console.log("uploading item:", params.item);
      return uploadViaXHR({ file: params.file, project: params.item.project, token, onProgress: updateProgress });
    },
    //invalidate query

    onMutate: async ({ item }) => {
      const key = ["files", accountId, item.project];
      await queryClient.cancelQueries({ queryKey: key });
      const prev = queryClient.getQueryData<FileItem[]>(key);
      queryClient.setQueryData<FileItem[]>(key, (old = []) => [...old, item]);
      return { key, prev, id: item.id };
    },

    onError: (_e, _v, ctx) => {
      //rollback to prev
      if (ctx?.prev) queryClient.setQueryData(ctx.key, ctx.prev);
    },

    onSuccess: (_saved, _v, ctx) => {
      if (ctx?.prev) {
        //ctx.prev.summary = summary;
        queryClient.setQueryData(ctx.key, [...ctx.prev, _saved]);
      }
    },
  });
}

export function useFiles(accountId: string, projectPath: string | undefined, jwt: string | undefined) {
  return useQuery({
    queryKey: ["files", accountId, projectPath],
    queryFn: () => fetchFiles(projectPath!, jwt!),
    enabled: Boolean(projectPath && jwt),
  });
}

export function useEstimate(fileId: string | undefined, jwt: string | undefined) {
  return useQuery({
    queryKey: ["files", "estimate", fileId],
    queryFn: () => {
      const result = fetchEstimate(fileId!, jwt!);
      if (!result) throw new Error("Estimate not found.");
      return result;
    },
    enabled: Boolean(fileId && jwt),
    retry: (failureCount, err) => {
      console.log("Checking for updated estimate...", err);
      return (err as any)?.status === 204 ? true : failureCount < 60; //60 sec
    },
    retryDelay: 2000,
  });
}

export function useFile(fileId?: string, jwt?: string) {
  console.log("use file");
  return useQuery({
    queryKey: ["files", fileId],
    queryFn: () => {
      const result = fetchFile(fileId!, jwt!);
      if (!result) throw new Error("File not found.");
      return result;
    },
    enabled: Boolean(fileId && jwt),
  });
}

export function useDeleteFile(accountId?: string, jwt?: string) {
  const queryClient = useQueryClient();
  const key = ["files", accountId] as const; // QueryKey

  type Ctx = { key: QueryKey; prev?: FileItem[] };

  return useMutation<void, Error, { fileId: string }, Ctx>({
    mutationFn: ({ fileId }) => deleteFile(fileId!, jwt!), // should return Promise<void>
    onMutate: async ({ fileId }) => {
      await queryClient.cancelQueries({ queryKey: key });
      const prev = queryClient.getQueryData<FileItem[]>(key);
      queryClient.setQueryData<FileItem[]>(key, (old = []) => old.filter((f) => f.id !== fileId));
      return { key, prev };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.prev) queryClient.setQueryData<FileItem[]>(ctx.key, ctx.prev);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: key });
    },
  });
}
